// Aircraft Item Details tab of the Components item record (H100; limitations editable
// 2026-10-07, Aaron: "need to be able to edit a compoent / copy and review from legacy").
//
// Legacy: mod_parts/includes/tabs/parts_tab_item_aircraft.inc (aviation template;
// shown only at a maintenance status and when the item is on an aircraft — see
// componentRecord.ts visibleComponentTabs).
//   1. Aircraft Details   ajax parts_item_aircraft_tab.inc:2-134 — aircraft reg,
//      component location, position (items_limitations active, aircraft_id != 0)
//      -> GET /v6/items/{id}/aircraft-details
//   2. Current Limitations  ajax :171-342 -> GET /v6/items/{id}/limitations, with the
//      columns legacy shows (:268-342): Based On, Period, Type, Finite Life, Warning,
//      Time Used Current Install, Date On / Due Off, @A/C Unit Fitted / Remove,
//      @Install TSN / TSO, Total TSN / TSO, Remaining. The computed ones come from the API.
//      Add / Edit / Delete (:531-660): (+) and ticks -> ACTIONS (the detail-tab rule,
//      Aaron 2026-10-06: no row pencil) -> ComponentLimitationSheet, or a confirmed delete;
//      POST / PATCH / DELETE /v6/items/{id}/limitations (app/items/limitations.py).
//   3. Current Inspection/s :923-975 (aircraft_inspection_log.component_id)
//      -> GET /v6/items/{id}/inspections
//   4. Current AD/SB :1327-1380 — aircraft_ad_log by component_id (what legacy's grid reads,
//      aircraft_templates_ads.inc:571-572) -> GET /v6/items/{id}/ads
//   5. Special Tasks :1784-1808 — aircraft_notes source_type 'item' -> GET /v6/items/{id}/special-tasks
//   Remove from aircraft (:22-35): the aircraft tab's removal sheet (ComponentRemoveSheet), here
//   too, on the fitment line.
// NOT BUILT here yet: edit location / position.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, Link, Stack, Typography } from "@mui/material";
import {
  DataGridPremium,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";

import { axios } from "../../../api";
import AlertDialog from "../../../components/AlertDialog";
import {
  ActionButton,
  CancelButton,
  CreateFab2,
} from "../../../components/Buttons";
import { ContentGridBox } from "../../../components/DatagridProContnet";
import type { ActionMenuItem } from "../../../components/datagrid/ActionMenu";
import { commonDataGridPremiumSx } from "../../../utils/dataGridSx";
import {
  formatTenantDate,
  useTenantDateFormat,
} from "../../../utils/tenantDate";
import {
  ComponentLimitationRow,
  isConditionAxis,
  isDateAxis,
} from "../componentLimitations";
import ComponentRemoveSheet from "../../../aircraft/ComponentRemoveSheet";
import ComponentLimitationSheet from "./ComponentLimitationSheet";
import SubgridActions from "./SubgridActions";

interface Fitment {
  aircraft_id: number;
  registration: string | null;
  location_id: number | null;
  location: string | null;
  position: string | null;
}

const num = (v: unknown) =>
  v === null || v === undefined || v === "" ? "" : String(Number(v));
/** Blank at 0 or missing — the Aircraft > Components tab's Remaining rule. */
const remaining = (units: unknown, days: unknown) => {
  if (units !== null && units !== undefined && Number(units) !== 0)
    return num(units);
  if (days !== null && days !== undefined && Number(days) !== 0)
    return `${days} days`;
  return "";
};
/** "a / b", dropping a side that is blank (legacy shows the pair in one cell). */
const pair = (a: string, b: string) =>
  [a, b].filter((x) => x !== "").join(" / ");
/** legacy shows "UNK" for a TSN of -1 (parts_item_aircraft_tab.inc:276). */
const tsn = (v: unknown) => (Number(v) === -1 ? "UNK" : num(v));

function limitationColumns(
  datePattern: string | undefined
): GridColDef<ComponentLimitationRow>[] {
  const day = (v: unknown) => formatTenantDate(v as string, datePattern);
  return [
    { field: "based_on", headerName: "Based On", flex: 1 },
    {
      field: "life",
      headerName: "Period",
      flex: 0.6,
      valueGetter: (_v, row) =>
        isConditionAxis(row.based_on) ? "N/A" : num(row.life),
    },
    { field: "limitation_type", headerName: "Type", flex: 0.9 },
    {
      field: "life_hours",
      headerName: "Finite Life",
      flex: 0.6,
      valueGetter: (_v, row) => num(row.life_hours) || "",
    },
    {
      field: "warning",
      headerName: "Warning",
      flex: 0.7,
      valueGetter: (_v, row) =>
        `${num(row.warning)} ${row.warning_unit ?? ""}`.trim(),
    },
    {
      field: "used_this_install",
      headerName: "Time Used Current Install",
      flex: 0.9,
      valueGetter: (_v, row) => num(row.used_this_install),
    },
    {
      field: "install_date",
      headerName: "Date On / Due Off",
      flex: 1.2,
      valueGetter: (_v, row) =>
        pair(
          day(row.install_date),
          isDateAxis(row.based_on) ? day(row.expiry_date) : ""
        ),
    },
    {
      field: "aircraft_install",
      headerName: "@A/C Unit Fitted / Remove",
      flex: 1.1,
      valueGetter: (_v, row) =>
        isDateAxis(row.based_on)
          ? ""
          : pair(
              num(row.aircraft_install),
              row.next_due ? num(row.next_due) : ""
            ),
    },
    {
      field: "total_life_used",
      headerName: "@Install TSN / TSO",
      flex: 0.9,
      valueGetter: (_v, row) =>
        pair(tsn(row.total_life_used), num(row.life_at_install)),
    },
    {
      field: "total_tsn",
      headerName: "Total TSN / TSO",
      flex: 0.9,
      valueGetter: (_v, row) =>
        pair(
          row.total_tsn === null ? "UNK" : num(row.total_tsn),
          num(row.total_tso)
        ),
    },
    {
      field: "remaining",
      headerName: "Remaining",
      flex: 0.8,
      valueGetter: (_v, row) =>
        row.remaining_units !== undefined || row.remaining_days !== undefined
          ? remaining(row.remaining_units, row.remaining_days)
          : remaining(row.units_remaining, (row as any).days_remaining),
    },
  ];
}

const inspectionColumns: GridColDef[] = [
  { field: "inspection_type", headerName: "Inspection Type", flex: 1 },
  { field: "inspection", headerName: "Inspection", flex: 1.4 },
  { field: "reference", headerName: "Reference", flex: 1 },
  { field: "based_on", headerName: "Based On", flex: 0.8 },
  {
    field: "period",
    headerName: "Period",
    flex: 0.6,
    valueFormatter: (value) => num(value),
  },
  {
    field: "warning",
    headerName: "Warning",
    flex: 0.7,
    valueGetter: (_v, row: any) =>
      `${num(row.warning)} ${row.warning_unit ?? ""}`.trim(),
  },
  { field: "last_date", headerName: "Last Inspection", flex: 0.9 },
  { field: "next_date", headerName: "Next Inspection", flex: 0.9 },
  {
    field: "used",
    headerName: "Time Used",
    flex: 0.7,
    valueFormatter: (value) => num(value),
  },
  {
    field: "remaining",
    headerName: "Remaining",
    flex: 0.8,
    valueGetter: (_v, row: any) =>
      remaining(row.units_remaining, row.days_remaining),
  },
];

const adColumns: GridColDef[] = [
  { field: "type", headerName: "Type", flex: 0.4 },
  { field: "number", headerName: "Number", flex: 0.9 },
  { field: "subject", headerName: "Subject", flex: 1.6 },
  { field: "based_on", headerName: "Based On", flex: 0.8 },
  { field: "period", headerName: "Period", flex: 0.5, valueFormatter: (value) => num(value) },
  { field: "status", headerName: "Status", flex: 0.7 },
  {
    field: "remaining",
    headerName: "Remaining",
    flex: 0.7,
    valueGetter: (_v, row: any) => remaining(row.units_remaining, row.days_remaining),
  },
  {
    field: "on_aircraft",
    headerName: "On the aircraft",
    flex: 0.7,
    valueGetter: (_v, row: any) => (row.on_aircraft ? "Yes" : "Off — frozen at removal"),
  },
];

const gridSx = {
  ...commonDataGridPremiumSx,
  borderLeft: "none",
  borderRight: "none",
};

function useRows<T>(url: string, label: string, reloadKey = 0): [T[], boolean] {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let current = true;
    setLoading(true);
    axios
      .get(url, { cache: false })
      .then(
        (resp) => current && setRows(Array.isArray(resp.data) ? resp.data : [])
      )
      .catch(
        () =>
          current &&
          enqueueSnackbar(`Error fetching ${label}`, { variant: "error" })
      )
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [enqueueSnackbar, label, url, reloadKey]);
  return [rows, loading];
}

interface Props {
  itemId: number;
  /** No parts Edit grant, or the item is inactive: read-only (H100 record gate). */
  locked?: boolean;
}

const ComponentAircraftDetails: React.FC<Props> = ({
  itemId,
  locked = true,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const datePattern = useTenantDateFormat();
  const [reloadKey, setReloadKey] = useState(0);
  const [fitments, fitLoading] = useRows<Fitment>(
    `/v6/items/${itemId}/aircraft-details`,
    "aircraft details"
  );
  const [limitations, limLoading] = useRows<ComponentLimitationRow>(
    `/v6/items/${itemId}/limitations`,
    "limitations",
    reloadKey
  );
  const [inspections, inspLoading] = useRows<any>(
    `/v6/items/${itemId}/inspections`,
    "inspections"
  );
  const [ads, adsLoading] = useRows<any>(`/v6/items/${itemId}/ads`, "ADs / SBs");
  const [specialTasks] = useRows<any>(`/v6/items/${itemId}/special-tasks`, "special tasks");
  const [removing, setRemoving] = useState<Fitment | null>(null);
  const fitmentText = useMemo(
    () => (fitLoading ? "Loading…" : null),
    [fitLoading]
  );
  const fitted =
    fitments.length > 0 || limitations.some((l) => Number(l.aircraft_id) > 0);

  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [sheet, setSheet] = useState<{
    open: boolean;
    row: ComponentLimitationRow | null;
  }>({ open: false, row: null });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const columns = useMemo(() => limitationColumns(datePattern), [datePattern]);
  const selectedRows = useMemo(
    () => limitations.filter((l) => selection.includes(l.id)),
    [limitations, selection]
  );
  const knownBasedOn = useMemo(
    () => limitations.map((l) => l.based_on ?? ""),
    [limitations]
  );

  const reload = useCallback(() => {
    setSelection([]);
    setReloadKey((k) => k + 1);
  }, []);

  const actions: ActionMenuItem[] = locked
    ? []
    : [
        {
          name: "Edit Limitation",
          requiresSelection: true,
          preventMultiSelect: true,
          multiSelectErrorMessage: "Select one limitation to edit.",
          onClick: () => setSheet({ open: true, row: selectedRows[0] ?? null }),
        },
        {
          name: "Delete Limitation/s",
          requiresSelection: true,
          onClick: () => setConfirmDelete(true),
        },
      ];

  const deleteSelected = async () => {
    setConfirmDelete(false);
    const results = await Promise.allSettled(
      selectedRows.map((l) =>
        axios.delete(`/v6/items/${itemId}/limitations/${l.id}`)
      )
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed)
      enqueueSnackbar(`${failed} limitation/s could not be deleted`, {
        variant: "error",
      });
    else enqueueSnackbar("Limitation/s deleted", { variant: "success" });
    reload();
  };

  return (
    <ContentGridBox>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Aircraft Details
      </Typography>
      <Box data-testid="component-fitment" sx={{ mb: 3 }}>
        {fitmentText ??
          (fitments.length === 0 ? (
            <Typography variant="body2">Not fitted to an aircraft.</Typography>
          ) : (
            fitments.map((f) => (
              <Stack
                key={`${f.aircraft_id}-${f.location_id}-${f.position}`}
                direction="row"
                spacing={4}
              >
                <Typography variant="body2">
                  Aircraft:{" "}
                  <Link href={`/app/aircraft/${f.aircraft_id}`}>
                    {f.registration || `#${f.aircraft_id}`}
                  </Link>
                </Typography>
                <Typography variant="body2">
                  Component Location: {f.location || "—"}
                </Typography>
                <Typography variant="body2">
                  Position: {f.position || "—"}
                </Typography>
                {!locked ? (
                  <Button size="small" onClick={() => setRemoving(f)}>
                    Remove from aircraft
                  </Button>
                ) : null}
              </Stack>
            ))
          ))}
      </Box>

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Current Limitations
      </Typography>
      {!locked ? (
        <Grid container sx={{ pb: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <CreateFab2
              size="small"
              title="Add Limitation"
              aria-label="Add Limitation"
              onClick={() => setSheet({ open: true, row: null })}
            />
            <SubgridActions
              popupId="component-limitations-actions"
              actions={actions}
              selectionCount={selection.length}
            />
          </Grid>
        </Grid>
      ) : null}
      <DataGridPremium
        autoHeight
        density="compact"
        rows={limitations}
        columns={columns}
        loading={limLoading}
        checkboxSelection={!locked}
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        hideFooter={limitations.length <= 15}
        sx={{ ...gridSx, mb: 3 }}
        aria-label="current-limitations"
      />

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Current Inspection/s
      </Typography>
      <DataGridPremium
        autoHeight
        density="compact"
        rows={inspections}
        columns={inspectionColumns}
        loading={inspLoading}
        hideFooter={inspections.length <= 15}
        sx={{ ...gridSx, mb: 3 }}
        aria-label="current-inspections"
      />

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Current AD/SB
      </Typography>
      <DataGridPremium
        autoHeight
        density="compact"
        rows={ads}
        columns={adColumns}
        loading={adsLoading}
        hideFooter={ads.length <= 15}
        sx={{ ...gridSx, mb: 3 }}
        aria-label="current-ad-sb"
      />

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Special Tasks
      </Typography>
      <Box data-testid="component-special-tasks" sx={{ mb: 1 }}>
        {specialTasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No special tasks.
          </Typography>
        ) : (
          specialTasks.map((t: any) => (
            <Typography key={t.id} variant="body2" color={t.active ? undefined : "text.secondary"}>
              {t.notes || `Task ${t.id}`}
              {t.based_on ? ` · ${t.based_on}` : ""}
              {t.active ? "" : " (closed at removal)"}
            </Typography>
          ))
        )}
      </Box>

      {removing ? (
        <ComponentRemoveSheet
          open
          aircraft={{ id: removing.aircraft_id, registration: removing.registration }}
          component={{ itemId, partNumber: null, serial: null, basedOns: limitations.map((l) => l.based_on) }}
          onClose={() => setRemoving(null)}
          // the record's fitment, tabs and status all change: re-read the whole record
          onRemoved={() => window.location.reload()}
        />
      ) : null}
      {/* mounted only while open: a closed sheet reads no config and keeps no draft (BP-89) */}
      {sheet.open ? (
        <ComponentLimitationSheet
          open
          itemId={itemId}
          row={sheet.row}
          fitted={fitted}
          knownBasedOn={knownBasedOn}
          onClose={() => setSheet({ open: false, row: null })}
          onSaved={() => {
            setSheet({ open: false, row: null });
            reload();
          }}
        />
      ) : null}
      <AlertDialog
        open={confirmDelete}
        title="Delete limitation/s?"
        description={`${selectedRows.length} limitation/s will be removed from this component. Their history is kept.`}
        handleClose={() => setConfirmDelete(false)}
      >
        <CancelButton onClick={() => setConfirmDelete(false)} />
        <ActionButton onClick={deleteSelected}>Delete</ActionButton>
      </AlertDialog>
    </ContentGridBox>
  );
};

export default ComponentAircraftDetails;
